import React, { useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { useSelector } from 'react-redux';

import FullCalendar from '@fullcalendar/react'; // must go before plugins
import listPlugin from '@fullcalendar/list'; // a plugin!
import timegridPlugin from '@fullcalendar/timegrid';
import daygridPlugin from '@fullcalendar/daygrid';
import multimonthPlugin from '@fullcalendar/multimonth';

// Only import the locales we actually support (EN, DE, FR, IT, ES)
// This significantly reduces bundle size compared to importing all ~50 locales
import deLocale from '@fullcalendar/core/locales/de';
import frLocale from '@fullcalendar/core/locales/fr';
import itLocale from '@fullcalendar/core/locales/it';
import esLocale from '@fullcalendar/core/locales/es';
// English is the default locale in FullCalendar, no need to import

const supportedLocales = [deLocale, frLocale, itLocale, esLocale];

import { selectFcConfig } from '../../slices/appState';


import { DateTime } from 'luxon';
import useElDimensions from '../../hooks/useElDimensions';
import useBookableCurator from '../../hooks/useBookableCurator';
import { useTranslation } from 'react-i18next';

import DefaultInterfacesHandler from '../handlers/DefaultInterfacesHandler';
import usePropsBeforeContext from '../../hooks/usePropsBeforeContext';
import { CalendarViewContext } from '../Calendar';
import useFirstBookbale from '../../hooks/useFirstBookbale';
import { CrossFade } from '../base/CrossFade';
import { LOADING_GRACE_MS, LOADING_MIN_DISPLAY_MS } from '../../hooks/useSmartLoading';

const parseBookables = function (curatedBookables, t, theme) {
  if (!curatedBookables) return [];

  const parsedDates = [];

  for (const [, timeslots] of curatedBookables) {
    for (const timeslot of timeslots) {
      let event;

      if (timeslot.renderAsCancelable) {
        // render as cancelable
        event = {
          start: timeslot.start.toISO({
            suppressMilliseconds: true,
          }),
          end: timeslot.end.toISO({
            suppressMilliseconds: true,
          }),
          title: t('cancellation.cancellable_appointment_highlight'),
          extendedProps: timeslot,
          borderColor: theme.palette.text.primary,
        };
      } else if (timeslot.renderAsBookable) {
        // render as bookable
        event = {
          start: timeslot.start.toISO({
            suppressMilliseconds: true,
          }),
          end: timeslot.end.toISO({
            suppressMilliseconds: true,
          }),
          title: '',
          extendedProps: timeslot,
        };
      } else if (timeslot.renderAsBlocker) {
        // render as blocker
        event = {
          title: t('appoinment_at_capacity'),
          display: 'background',
          backgroundColor: theme.palette.action.disabledBackground,
          start: timeslot.start.toISO({
            suppressMilliseconds: true,
          }),
          end: timeslot.end.toISO({
            suppressMilliseconds: true,
          }),
          extendedProps: timeslot,
        };
      }
      parsedDates.push(event);
    }
  }

  return parsedDates;
};

export default function FullCalendarView(props) {
  const { t, i18n } = useTranslation();
  const {
    isFetching,
    bookables,
    product,
    products,
    resource,
    publicDataMap,
    openResourcePage,
    openProductPage,
    openBookingPage,
    height,
    suppressDefaultInterfaces = false,
  } = usePropsBeforeContext(props, CalendarViewContext);

  const readyFcConfig = useSelector((state) => selectFcConfig(state));
  const calRef = React.useRef(null);
  const [ref, { width }] = useElDimensions();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery((th) => th.breakpoints.down('sm'));
  const [oldProduct, setOldProduct] = React.useState();
  const curatedBookables = useBookableCurator({
    bookables,
  });
  const { bookable: firstBookable } = useFirstBookbale(curatedBookables);

  const events = parseBookables(curatedBookables, t, theme);

  useEffect(() => {
    if (!oldProduct !== product && firstBookable && calRef.current) {
      const calApi = calRef.current.getApi();
      calApi.gotoDate(DateTime.fromISO(firstBookable?.start).toISODate());

      let scrollTime = DateTime.fromISO(firstBookable?.start).minus({
        hours: 5,
      });

      // Define the threshold time (8 AM) based on the DateTime's current date
      let eightAM = DateTime.fromObject({
        hour: 8,
        minute: 0,
        second: 0,
        millisecond: 0,
      });

      // Check if the new time is earlier than 8 AM
      if (scrollTime > eightAM) {
        calApi.scrollToTime(scrollTime.toFormat('HH:mm'));
      }

      setOldProduct(product);
    }
  }, [events, firstBookable, firstBookable?.start, oldProduct, product]);

  const shouldDisplayResourceSelect = useCallback(() => {
    return (
      openResourcePage &&
      isSmallScreen &&
      publicDataMap &&
      Object.keys(publicDataMap).length > 1
    );
  }, [isSmallScreen, openResourcePage, publicDataMap]);

  return (
    <CrossFade
      showSkeleton={isFetching || (!product && products?.length > 0)}
      graceMs={LOADING_GRACE_MS}
      minDisplayMs={LOADING_MIN_DISPLAY_MS}
      skeleton={<LoadingSkeleton height={height} />}
      height={height}
    >
      <Box ref={ref} sx={{ flexGrow: 1 }}>
        <FullCalendar
          ref={calRef}
          locales={supportedLocales}
          locale={i18n.resolvedLanguage}
          height={height}
          timeZone="utc"
          plugins={[
            listPlugin,
            timegridPlugin,
            daygridPlugin,
            multimonthPlugin,
          ]}
          customButtons={{
            productSelection: {
              text: product?.name ?? t('product_selection_headline'),
              click: () => {
                openProductPage();
              },
            },
            resourceSelection: {
              text: resource?.name ?? t('resource_selection_headline'),
              click: () => {
                openResourcePage();
              },
            },
          }}
          headerToolbar={{
            left: shouldDisplayResourceSelect()
              ? 'resourceSelection productSelection'
              : 'productSelection',
            right: 'today prev,next',
          }}
          noEventsText={t('noEventsMessage')}
          events={events}
          eventClick={(event) => {
            const timeslot = { ...event.event.extendedProps };
            timeslot.start = DateTime.fromISO(event.event.startStr);
            timeslot.end = DateTime.fromISO(event.event.endStr);
            openBookingPage(timeslot);
          }}
          windowResize={() => {
            // only enable ui responsiveness if it's properly configured
            if (readyFcConfig.smallView && readyFcConfig.largeView) {
              if (width < 601) {
                calRef?.current?.getApi()?.changeView(readyFcConfig.smallView);
              } else {
                calRef?.current?.getApi()?.changeView(readyFcConfig.largeView);
              }
            }
          }}
          {...readyFcConfig}
        />
        {!suppressDefaultInterfaces && <DefaultInterfacesHandler />}
      </Box>
    </CrossFade>
  );
}

const LoadingSkeleton = ({ height = 500 }) => {
  const [count, setCount] = React.useState();

  // Array of days in the week
  const days = [
    'Sun 3/2',
    'Mon 3/3',
    'Tue 3/4',
    'Wed 3/5',
    'Thu 3/6',
    'Fri 3/7',
    'Sat 3/8',
  ];
  useEffect(() => {
    setCount(
      Math.floor((height - 92) /* combined height of other elements */ / 56),
    );
  }, [height]);

  return (
    <Box sx={{ width: '100%' }}>
      <Grid
        container
        sx={{
          justifyContent: 'space-between',
        }}
      >
        {/* Prod Button */}
        <Grid item xs="auto">
          <Skeleton variant="rounded" width="160px" height="38px" />
        </Grid>
        {/* Nav Buttons */}
        <Grid item xs="auto">
          <Skeleton variant="rounded" width="160px" height="38px" />
        </Grid>
      </Grid>
      {/* Day Headers */}
      <Grid container spacing={2}>
        {days.map((day, index) => (
          <Grid item xs={1.5} key={index}>
            <Skeleton variant="text" width="100%" />
          </Grid>
        ))}
      </Grid>
      {/* Time Slot Rows */}
      {[...Array(count)].map((_, rowIndex) => (
        <Grid container spacing={2} key={rowIndex}>
          <Grid item xs={1.5}>
            <Skeleton variant="text" width="100%" />
          </Grid>
          {days.map((_, colIndex) => (
            <Grid item xs={1.5} key={colIndex}>
              <Skeleton variant="rectangular" width="100%" height={56} />
            </Grid>
          ))}
        </Grid>
      ))}
    </Box>
  );
};
